import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import RenderHTML from 'react-native-render-html';
import Svg, { Path } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import { IconCloseSvg } from '../../../assets/inventory';
import NotificationBellPng from '../../../assets/notification-bell.png';
import { getPlayersDirectory, PlayersDirectoryItem } from '../../../services/api';
import { getSocket } from '../../../services/socket';
import { NotificationItem } from '../../../store/types';
import {
	markAllSeenRequest,
	markReadRequest,
	markSeenRequest,
	notificationsRequest,
} from '../../../store/userNotifications/notificationsActions';
import {
	selectNotifications,
	selectNotificationsLoading,
} from '../../../store/userNotifications/notificationsReducer';
import { formatDate } from '../../../utils/helpers/date';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import {
	PANEL_FILL,
	PANEL_RIM,
	ROW_FILL,
	TOURNAMENT_FONT as FONT,
	tournamentColors as colors,
} from '../Tournaments/tournamentModalTheme';
import ProfileRedesignModal from './ProfileRedesignModal';
import RedesignModal from './RedesignModal';
import { useRedesignScale } from './useRedesignScale';

interface MessagesModalProps {
	onClose: () => void;
	/** Opens the Friends tab, where an incoming friend request can be accepted or rejected. */
	onOpenFriendRequests?: () => void;
}

/** The url the players service puts on a "friend request received" notification. */
const FRIEND_REQUESTS_URL = '/friends/requests';

type BackendNotification = any;

type UiNotification = {
	id: string;
	titleKey?: string;
	bodyKey?: string;
	valueParts: string[];
	url?: string | null;
	raw: BackendNotification;
};

function splitValue(value: any): string[] {
	if (value == null) return [];

	if (typeof value === 'string') {
		return value
			.split(',')
			.map(s => s.trim())
			.filter(Boolean);
	}

	return [];
}

function replacePercents(text: string, parts: string[]) {
	let out = text;
	let idx = 0;
	while (out.includes('%%') && idx < parts.length) {
		out = out.replace('%%', parts[idx]);
		idx += 1;
	}
	return out;
}

const DUEL_LINK_PREFIX = '/duels/';

const isDuelLink = (item: { url?: unknown }) =>
	typeof item.url === 'string' && item.url.startsWith(DUEL_LINK_PREFIX);

const duelRouteFromUrl = (url: string) => {
	const [, , section, id] = url.split('?')[0].split('/');
	if (section === 'season') return { name: 'DuelSeason' as const, params: undefined };
	if (section === 'result' && id) {
		return { name: 'DuelResult' as const, params: { duelId: decodeURIComponent(id) } };
	}
	if (section === 'rewards' && id) {
		return { name: 'DuelReward' as const, params: { entitlementId: decodeURIComponent(id) } };
	}
	return null;
};

const getInviteActionFromUrl = (url?: string | null) => {
	if (!url) return null;

	const [pathPart, queryPart = ''] = url.split('?');
	const roomMatch = pathPart.match(/^\/room\/([^/]+)\/([^/]+)$/);
	if (!roomMatch) return null;

	const query = new URLSearchParams(queryPart);
	const inviteId = query.get('inviteId');
	const tableId = query.get('tableId');

	if (!inviteId || !tableId) return null;

	return { inviteId, tableId };
};

/**
 * The notifications inbox, opened from the bell. There is no Figma frame for it, so it is put
 * together from the redesign's own pieces, like the jackpot history: the tournament sheet's panel
 * and title bar around a sunken list of row plates in the leaderboard's style - a gold-ringed icon,
 * the gold title, the message and its time, and a delete button. Unread rows get the gold rim and a
 * dot. Numbers are in the 390-wide mobile design space and scaled once.
 */
const PANEL = { height: 600, radius: 10, paddingX: 12, paddingY: 20, gap: 16 };
/** List hairline, "border/red" like the leaderboard frame's. */
const LIST_RIM = '#6B1A10';
/** Unread rows: the tournament cancel button's red ramp under a gold rim. */
const UNREAD_FILL: Array<[string, string]> = [
	['0', '#5A0B03'],
	['1', '#1F0402'],
];

const formatTime = (dateString: string) =>
	new Date(dateString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** "14:05" today, otherwise the date and the time. */
const formatStamp = (dateString: string) => {
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return '';
	const sameDay = date.toDateString() === new Date().toDateString();
	return sameDay ? formatTime(dateString) : `${formatDate(dateString)} ${formatTime(dateString)}`;
};

const TrashIcon = ({ size, color }: { size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 24 24'>
		<Path
			d='M4 7h16M9.5 7V5.2c0-.66.54-1.2 1.2-1.2h2.6c.66 0 1.2.54 1.2 1.2V7M6.5 7l.8 11.6c.07 1.05.95 1.9 2 1.9h5.4c1.05 0 1.93-.85 2-1.9L17.5 7M10.3 11v5.5M13.7 11v5.5'
			fill='none'
			stroke={color}
			strokeWidth={1.8}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

const InvitePillArrow = ({ size, color }: { size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 12 12'>
		<Path
			d='m4 2.25 3.75 3.75L4 9.75'
			fill='none'
			stroke={color}
			strokeWidth={1.5}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

const MessagesModal = ({ onClose, onOpenFriendRequests }: MessagesModalProps) => {
	const auth = useSelector((state: any) => state.auth);
	const dispatch = useDispatch();
	const navigation = useNavigation<any>();
	const { t } = useTranslation();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);
	const [selectedPlayer, setSelectedPlayer] = useState<PlayersDirectoryItem | null>(null);

	const loading = useSelector(selectNotificationsLoading);
	const rawNotifications: NotificationItem[] = useSelector(selectNotifications);

	useEffect(() => {
		dispatch(notificationsRequest());
	}, [dispatch]);

	const uiMessages: UiNotification[] = useMemo(() => {
		if (!Array.isArray(rawNotifications)) return [];
		return rawNotifications
			.filter(Boolean)
			.map((n: any) => {
				const url = n?.url ?? n?.template?.url ?? null;

				return {
					id: String(n?.id ?? ''),

					titleKey: n?.template?.title ?? n?.title,
					bodyKey: n?.template?.message ?? n?.body,

					valueParts: splitValue(n?.valueParts),
					url,
					raw: n,
				};
			})
			.filter(x => !!x.id);
	}, [rawNotifications]);

	const getTexts = (item: UiNotification) => {
		const titleTemplate = item.titleKey ? t(item.titleKey, { defaultValue: item.titleKey }) : '';

		const bodyTemplate = item.bodyKey ? t(item.bodyKey, { defaultValue: item.bodyKey }) : '';

		const title = titleTemplate.includes('%%')
			? replacePercents(titleTemplate, item.valueParts)
			: titleTemplate;

		const body = bodyTemplate.includes('%%')
			? replacePercents(bodyTemplate, item.valueParts)
			: bodyTemplate;

		return { title, body };
	};

	const handleClose = () => {
		const unreadNotifications = rawNotifications.filter(n => !n.read);
		if (unreadNotifications.length > 0) {
			dispatch(markAllSeenRequest());
		}
		onClose();
	};

	const openDuelLink = (notificationId: string, url: string) => {
		dispatch(markSeenRequest({ notificationId }));
		const state = navigation.getState?.();
		const below = state?.routes?.[(state?.index ?? 0) - 1]?.name;
		handleClose();
		if (below === 'Game') return;
		const route = duelRouteFromUrl(url);
		if (route) navigation.navigate(route.name, route.params);
	};

	const respondToInviteFromUrl = (url: string, response: 'accept' | 'reject') => {
		const inviteAction = getInviteActionFromUrl(url);
		if (!inviteAction || !auth.user?.id) return null;

		const socket = getSocket(String(auth.user.id), String(auth.user.username));
		socket.emit('svara:respondToInvite', {
			inviteId: inviteAction.inviteId,
			response,
			table: inviteAction.tableId,
		});

		return inviteAction;
	};

	const isFriendRequest = (item: UiNotification) => item.url === FRIEND_REQUESTS_URL;

	const openFriendRequests = (item: UiNotification) => {
		if (!item.raw?.read) dispatch(markSeenRequest({ notificationId: item.id }));
		onOpenFriendRequests?.();
	};

	const openInviterProfile = async (item: UiNotification) => {
		const username = item.valueParts[0];
		if (!username) return;
		if (!item.raw?.read) dispatch(markSeenRequest({ notificationId: item.id }));

		try {
			const response = await getPlayersDirectory({ filter: 'ALL', query: username });
			const player = response.items.find(
				candidate => candidate.username?.toLowerCase() === username.toLowerCase(),
			);
			if (player) setSelectedPlayer(player);
		} catch {
			// A deleted or renamed inviter no longer has a profile to open.
		}
	};

	const deleteMessage = (item: UiNotification) => {
		if (item.url && !isFriendRequest(item)) {
			respondToInviteFromUrl(item.url, 'reject');
		}
		dispatch(markReadRequest({ notificationId: item.id }));
	};

	const rows = uiMessages
		.map(item => ({ item, ...getTexts(item) }))
		.filter(row => row.title || row.body);

	const status =
		loading && !rows.length ? (
			<ActivityIndicator color={colors.gold300} />
		) : !rows.length ? (
			<View style={styles.empty}>
				<Image source={NotificationBellPng} style={styles.emptyBell} resizeMode='contain' />
				<Text style={styles.status}>
					{t('T_SITE_NOTIFICATION_NO_NOTIFICATIONS', { defaultValue: 'No new messages' })}
				</Text>
			</View>
		) : null;

	return (
		<>
			<RedesignModal
				scale={scale}
				onClose={handleClose}
				panel={{
					height: PANEL.height,
					radius: PANEL.radius,
					fill: PANEL_FILL,
					rim: PANEL_RIM,
					overScreen: true,
				}}
			>
				<View style={styles.body}>
					<View style={styles.titleBar}>
						<Text style={styles.title} numberOfLines={1}>
							{t('T_SITE_NOTIFICATION_INBOX', { defaultValue: 'Notifications' }).toUpperCase()}
						</Text>
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_CLOSE_NOTIFICATIONS', {
								defaultValue: 'Close notifications',
							})}
							onPress={handleClose}
							hitSlop={8}
							style={styles.close}
						>
							<IconCloseSvg width={24 * scale} height={24 * scale} />
						</Pressable>
					</View>

					<View style={styles.list}>
						<GradientFill stops={ROW_FILL} />
						{status ? (
							<View style={styles.statusBox}>{status}</View>
						) : (
							<RedesignScrollView
								contentContainerStyle={styles.rows}
								indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
							>
								{rows.map(({ item, title, body }) => {
									const unread = !item.raw?.read;
									const inviteUsername = getInviteActionFromUrl(item.url)
										? item.valueParts[0]
										: null;
									const rowTitle = inviteUsername
										? t('T_SITE_NOTIFICATION_INVITE_FROM', { defaultValue: 'Invite from' })
										: title;
									return (
										<View key={item.id} style={[styles.row, unread && styles.rowUnread]}>
											<GradientFill stops={unread ? UNREAD_FILL : ROW_FILL} />
											<View style={styles.iconRing}>
												<Image
													source={NotificationBellPng}
													style={styles.icon}
													resizeMode='contain'
												/>
												{unread ? <View style={styles.unreadDot} /> : null}
											</View>
											<Pressable
												accessibilityRole={
													isFriendRequest(item) || isDuelLink(item) ? 'button' : undefined
												}
												accessibilityLabel={
													isFriendRequest(item) || isDuelLink(item) ? title : undefined
												}
												disabled={
													!isDuelLink(item) && (!isFriendRequest(item) || !onOpenFriendRequests)
												}
												onPress={() =>
													isDuelLink(item)
														? openDuelLink(item.id, item.url as string)
														: openFriendRequests(item)
												}
												style={styles.textCell}
											>
												{!!rowTitle && (
													<View style={styles.rowHeader}>
														<Text style={styles.rowTitle} numberOfLines={2}>
															{rowTitle}
														</Text>
														{inviteUsername ? (
															<Pressable
																accessibilityRole='button'
																accessibilityLabel={t('T_SITE_A11Y_OPEN_PLAYER_PROFILE', {
																	defaultValue: `Open ${inviteUsername}'s profile`,
																})}
																onPress={() => openInviterProfile(item)}
																style={styles.invitePill}
															>
																<Text style={styles.invitePillText} numberOfLines={1}>
																	{inviteUsername}
																</Text>
																<InvitePillArrow size={12 * scale} color={colors.gold200} />
															</Pressable>
														) : null}
													</View>
												)}
												{!!body && (
													<RenderHTML
														contentWidth={226 * scale}
														source={{ html: `<span>${body}</span>` }}
														baseStyle={styles.html}
														tagsStyles={{ b: styles.htmlBold, span: styles.html }}
													/>
												)}
												{!!item.raw?.createdAt && (
													<Text style={styles.stamp}>{formatStamp(item.raw.createdAt)}</Text>
												)}
											</Pressable>
											<Pressable
												accessibilityRole='button'
												accessibilityLabel={t('T_SITE_A11Y_DELETE_NOTIFICATION', {
													defaultValue: 'Delete notification',
												})}
												onPress={() => deleteMessage(item)}
												hitSlop={8}
												style={styles.delete}
											>
												<TrashIcon size={18 * scale} color={colors.secondary} />
											</Pressable>
										</View>
									);
								})}
							</RedesignScrollView>
						)}
					</View>
				</View>
			</RedesignModal>
			{selectedPlayer ? (
				<ProfileRedesignModal
					onClose={() => setSelectedPlayer(null)}
					player={{
						rank: 0,
						playerId: selectedPlayer.id,
						username: selectedPlayer.username ?? '',
						avatarFileId: selectedPlayer.avatarFileId,
						level: selectedPlayer.level,
						isVip: selectedPlayer.isVip,
						isFriend: selectedPlayer.friendshipStatus === 'FRIENDS',
						friendshipStatus: selectedPlayer.friendshipStatus,
						friendRequestId: selectedPlayer.friendRequestId,
					}}
				/>
			) : null}
		</>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const rim = Math.max(1, scale);
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		titleBar: { flexDirection: 'row', alignItems: 'center', gap: s(8) },
		title: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(24),
			lineHeight: s(24 * 1.3),
			color: colors.gold200,
		},
		close: { width: s(32), height: s(32), alignItems: 'center', justifyContent: 'center' },
		list: {
			flex: 1,
			minHeight: 0,
			paddingHorizontal: s(6),
			paddingVertical: s(8),
			borderRadius: s(12),
			borderWidth: rim,
			borderColor: LIST_RIM,
			overflow: 'hidden',
		},
		// 10 on the right keeps the rows clear of the scroll bar.
		rows: { gap: s(6), paddingTop: s(2), paddingRight: s(10), paddingBottom: s(2) },
		row: {
			minHeight: s(64),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(12),
			paddingHorizontal: s(10),
			paddingVertical: s(10),
			borderRadius: s(8),
			borderWidth: rim,
			borderColor: colors.rowBorder,
			overflow: 'hidden',
		},
		rowUnread: { borderColor: colors.buttonRim },
		iconRing: {
			width: s(40),
			height: s(40),
			borderRadius: s(20),
			borderWidth: s(2),
			borderColor: colors.buttonRim,
			backgroundColor: colors.sunken,
			alignItems: 'center',
			justifyContent: 'center',
		},
		icon: { width: s(24), height: s(24) },
		unreadDot: {
			position: 'absolute',
			top: -s(2),
			right: -s(2),
			width: s(10),
			height: s(10),
			borderRadius: s(5),
			borderWidth: s(1.5),
			borderColor: colors.sunken,
			backgroundColor: '#FF3B2F',
		},
		// The row is 342 wide on the 366 list: less its padding, the 40 icon, the 32 delete button
		// and the two 12 gaps leaves 226 for the copy.
		textCell: { flex: 1, minWidth: 0, width: s(226), gap: s(2) },
		rowHeader: { flexDirection: 'row', alignItems: 'center', columnGap: s(6) },
		rowTitle: {
			flexShrink: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(17),
			color: colors.gold50,
		},
		invitePill: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(2),
			maxWidth: s(94),
			paddingVertical: s(3),
			paddingLeft: s(7),
			paddingRight: s(4),
			borderRadius: s(10),
			borderWidth: rim,
			borderColor: colors.buttonRim,
			backgroundColor: '#4D1A08',
		},
		invitePillText: {
			flexShrink: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(12),
			lineHeight: s(14),
			color: colors.gold100,
		},
		html: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(12),
			lineHeight: s(15),
			color: colors.secondary,
		},
		htmlBold: { fontWeight: '800', color: colors.gold100 },
		stamp: {
			marginTop: s(2),
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(11),
			lineHeight: s(13),
			color: colors.muted,
		},
		delete: {
			width: s(32),
			height: s(32),
			borderRadius: s(8),
			borderWidth: rim,
			borderColor: colors.borderSubtle,
			backgroundColor: colors.sunken,
			alignItems: 'center',
			justifyContent: 'center',
		},
		statusBox: { flex: 1, alignItems: 'center', justifyContent: 'center' },
		empty: { alignItems: 'center', gap: s(12) },
		emptyBell: { width: s(56), height: s(56), opacity: 0.85 },
		status: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.muted,
			textAlign: 'center',
		},
	});
};

export default MessagesModal;
